import {
    forwardRef,
    useCallback,
    useEffect,
    useId,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
    ReactNode,
} from 'react';
import { Box, CircularProgress, SxProps } from '@mui/material';
// @ts-ignore — Vite worker import
import EngineWorker from '../../../custom-components/Editor/optimized.video.engine.worker.ts?worker';
import {
    CHANNEL_LENGTH,
    CONFIG,
    createSharedBuffer,
    getMemoryViews,
    MEM_MAP,
    STAMP_LENGTH,
} from '../../../custom-components/Editor/SharedConfig';
import {
    SMOOTH_PLAYER_LOOP_PROGRAM,
    SMOOTH_PLAYER_TIMELINE_FPS,
} from '../../../constants';
import { usePlaybackCoordinator } from '../../../contexts/PlaybackCoordinatorContext';
import {
    JumpPlayerState,
    JumpVideoPlayerRef,
} from '../JumpVideoPlayer/JumpPlayerTypes';
import {
    frameToItemIndex,
    ItemRange,
    ProgramItem,
    programToOverlays,
} from './program-mapping';
// @ts-ignore — JS profiling util
import { useRenderProfiler, profEffect } from '@/utils/renderProfiler';

const AUDIO_PROCESSOR_URL = new URL(
    '../../../custom-components/Editor/clock-processor.js',
    import.meta.url,
).href;

interface RenderControlsArg {
    state: JumpPlayerState;
}

interface Props {
    program: ProgramItem[];
    poster?: string;
    /** Render fullscreen controls. Receives the current player state. */
    renderControls?: (arg: RenderControlsArg) => ReactNode;
    sx?: SxProps;
    /** Override the global loop default for this card. */
    loopProgram?: boolean;
    /** Called whenever the player's isPlaying state changes, including external pauses from the coordinator. */
    onPlayStateChange?: (isPlaying: boolean) => void;
}

/**
 * Drop-in replacement for `JumpVideoPlayer` that reuses the editor's
 * worker/decoder engine for smooth playback across cut and source boundaries.
 *
 * Coordinator-driven: at most one card is "actively playing" at a time, and
 * the number of resident decode workers is LRU-capped via
 * `PlaybackCoordinatorContext`.
 */
const SmoothJumpVideoPlayer = forwardRef<JumpVideoPlayerRef, Props>(
    ({ program, poster, renderControls, sx, loopProgram, onPlayStateChange }, ref) => {
        useRenderProfiler('SmoothJumpVideoPlayer', { program, poster, renderControls, sx, loopProgram, onPlayStateChange });

        const cardId = useId();
        const coordinator = usePlaybackCoordinator();

        // Stable container for the canvas — we replace the child <canvas>
        // element via React `key` whenever the worker is (re)spawned, since
        // `transferControlToOffscreen` can only be called once per canvas.
        const canvasHolderRef = useRef<HTMLDivElement>(null);
        const canvasRef = useRef<HTMLCanvasElement | null>(null);
        // Sibling 2D canvas that holds the last-painted frame after eviction.
        const stillCanvasRef = useRef<HTMLCanvasElement | null>(null);
        const [showStill, setShowStill] = useState(false);
        const [canvasGen, setCanvasGen] = useState(0);

        // Worker / shared state lifetime (created lazily on first play).
        const workerRef = useRef<Worker | null>(null);
        const stateViewRef = useRef<Int32Array | null>(null);
        const audioCtxRef = useRef<AudioContext | null>(null);
        const workletNodeRef = useRef<AudioWorkletNode | null>(null);
        const gainNodeRef = useRef<GainNode | null>(null);

        // Saved playhead in samples for "resume after eviction" + "resume after
        // tab return". Updated on pause and on eviction; read on next play.
        const lastSamplesRef = useRef<number>(0);

        // Local player state. Drives the UI but is also pushed to the
        // coordinator so other cards can force a pause from outside.
        const [isPlaying, setIsPlaying] = useState(false);
        const [isFullscreen, setIsFullscreen] = useState(false);
        const [itemIndex, setItemIndex] = useState(0);
        const [isBuffering, setIsBuffering] = useState(false);
        const bufferingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
        // Use refs alongside state so async handlers see the latest value
        // without depending on stale closures.
        const isPlayingRef = useRef(false);
        const itemIndexRef = useRef(0);
        const mutedRef = useRef(true); // start muted; matches clipCard hover model
        const volumeRef = useRef(1);

        // Synthetic overlay/timeline derived from `program`.
        const { overlays, itemRanges, totalFrames } = useMemo(
            () => programToOverlays(cardId, program ?? [], SMOOTH_PLAYER_TIMELINE_FPS),
            [cardId, program],
        );
        const itemRangesRef = useRef<ItemRange[]>(itemRanges);
        itemRangesRef.current = itemRanges;
        const hasPlayable = totalFrames > 0 && overlays.length > 0;

        // ── Lifecycle helpers ────────────────────────────────────────────────

        const updateItemIndexFromCurrent = useCallback(() => {
            const sv = stateViewRef.current;
            if (!sv) return;
            const samples = Atomics.load(sv, MEM_MAP.MASTER_CLOCK);
            const seconds = samples / CONFIG.SAMPLE_RATE;
            const frame = Math.round(seconds * SMOOTH_PLAYER_TIMELINE_FPS);
            const idx = frameToItemIndex(itemRangesRef.current, frame);
            if (idx !== itemIndexRef.current) {
                itemIndexRef.current = idx;
                setItemIndex(idx);
            }
        }, []);

        const teardownWorker = useCallback((opts?: { skipUnregister?: boolean }) => {
            if (bufferingTimeoutRef.current) clearTimeout(bufferingTimeoutRef.current);
            bufferingTimeoutRef.current = null;
            setIsBuffering(false);

            const worker = workerRef.current;
            if (worker) {
                try {
                    worker.postMessage({ type: 'SHUTDOWN' });
                } catch {
                    try { worker.terminate(); } catch {}
                }
                setTimeout(() => {
                    try { worker.terminate(); } catch {}
                }, 1500);
            }
            workerRef.current = null;
            stateViewRef.current = null;

            // Tear down audio chain too — recreated next time we spawn.
            try { workletNodeRef.current?.disconnect(); } catch {}
            workletNodeRef.current = null;
            try { gainNodeRef.current?.disconnect(); } catch {}
            gainNodeRef.current = null;
            if (audioCtxRef.current) {
                const ctx = audioCtxRef.current;
                audioCtxRef.current = null;
                void ctx.close().catch(() => {});
            }

            if (!opts?.skipUnregister) {
                coordinator.unregisterResident(cardId);
            }
        }, [cardId, coordinator]);

        const captureStillAndTeardown = useCallback(async () => {
            const worker = workerRef.current;
            if (!worker) {
                teardownWorker();
                return;
            }
            // Save playhead for resume.
            const sv = stateViewRef.current;
            if (sv) lastSamplesRef.current = Atomics.load(sv, MEM_MAP.MASTER_CLOCK);

            const requestId = Math.random().toString(36).slice(2);
            const bitmap: ImageBitmap | null = await new Promise((resolve) => {
                const timeout = setTimeout(() => resolve(null), 250);
                const handler = (e: MessageEvent) => {
                    if (e.data?.type === 'SNAPSHOT' && e.data?.requestId === requestId) {
                        clearTimeout(timeout);
                        worker.removeEventListener('message', handler);
                        resolve(e.data.bitmap ?? null);
                    }
                };
                worker.addEventListener('message', handler);
                worker.postMessage({ type: 'TAKE_SNAPSHOT', requestId });
            });

            let stillCaptured = false;
            if (bitmap && stillCanvasRef.current) {
                const ctx = stillCanvasRef.current.getContext('2d');
                if (ctx) {
                    stillCanvasRef.current.width = bitmap.width;
                    stillCanvasRef.current.height = bitmap.height;
                    ctx.drawImage(bitmap, 0, 0);
                    stillCaptured = true;
                }
                try { bitmap.close(); } catch {}
            }
            // Only reveal the still layer if we actually drew something; an
            // empty still over the poster looks worse than the poster alone.
            if (stillCaptured) setShowStill(true);
            teardownWorker();
        }, [teardownWorker]);

        // Coordinator handle. We rebuild it whenever cardId changes (never in
        // practice — useId is stable). The actual callbacks read from refs so
        // they see the latest implementations.
        const forcePauseRef = useRef<() => void>(() => {});
        const forceEvictRef = useRef<() => void>(() => {});

        // ── Worker spawn ─────────────────────────────────────────────────────

        const ensureWorker = useCallback(async (): Promise<boolean> => {
            if (workerRef.current) return true;
            if (!hasPlayable) return false;

            // If the canvas was previously transferred-controlled, force a
            // remount so we have a fresh canvas to transfer.
            if (!canvasRef.current || canvasRef.current.dataset.transferred === '1') {
                setCanvasGen((g) => g + 1);
                // Wait one microtask for React to commit the new <canvas>.
                await Promise.resolve();
                await new Promise((r) => requestAnimationFrame(() => r(null)));
            }
            const canvas = canvasRef.current;
            if (!canvas) return false;

            const sab = createSharedBuffer();
            const { stateView } = getMemoryViews(sab);
            stateViewRef.current = stateView;

            const initialSamples = lastSamplesRef.current || 0;
            Atomics.store(stateView, MEM_MAP.MASTER_CLOCK, initialSamples);
            Atomics.store(stateView, MEM_MAP.IS_PLAYING, 0);
            Atomics.store(stateView, MEM_MAP.READ_PTR, initialSamples % CHANNEL_LENGTH);
            Atomics.store(stateView, MEM_MAP.WRITE_PTR, initialSamples % CHANNEL_LENGTH);
            Atomics.store(stateView, MEM_MAP.AUDIO_EPOCH, 1);
            Atomics.store(stateView, MEM_MAP.AUDIO_VALID_UNTIL, initialSamples);

            const worker = new EngineWorker() as Worker;
            workerRef.current = worker;
            worker.addEventListener('error', (e) =>
                console.error('[SmoothJumpVideoPlayer] worker error', e),
            );
            worker.onmessage = (e) => {
                if (e.data?.type !== 'BUFFERING') return;
                const buffering = Boolean(e.data.payload);
                if (buffering) {
                    bufferingTimeoutRef.current = setTimeout(() => setIsBuffering(true), 200);
                } else {
                    if (bufferingTimeoutRef.current) clearTimeout(bufferingTimeoutRef.current);
                    setIsBuffering(false);
                }
            };

            const offscreen = canvas.transferControlToOffscreen();
            canvas.dataset.transferred = '1';
            worker.postMessage(
                {
                    type: 'INIT',
                    sab,
                    canvas: offscreen,
                    overlays,
                    opts: {
                        timelineFps: SMOOTH_PLAYER_TIMELINE_FPS,
                        endBehavior:
                            (loopProgram ?? SMOOTH_PLAYER_LOOP_PROGRAM) ? 'loop' : 'stop',
                        audioEnabled: !mutedRef.current,
                    },
                },
                [offscreen],
            );

            // Audio worklet (required for MASTER_CLOCK to advance even when
            // we're silent — the worklet ticks the clock).
            try {
                const ctx = new AudioContext({ sampleRate: CONFIG.SAMPLE_RATE });
                audioCtxRef.current = ctx;
                await ctx.audioWorklet.addModule(AUDIO_PROCESSOR_URL);
                if (ctx.state === 'suspended') await ctx.resume().catch(() => {});

                const debugAudio =
                    String((import.meta as any).env?.VITE_DEBUG_AUDIO_PIPELINE ?? '')
                        .toLowerCase() === 'true';

                const node = new AudioWorkletNode(ctx, 'clock-processor', {
                    processorOptions: {
                        sab,
                        headerInts: MEM_MAP.HEADER_INTS,
                        channels: CONFIG.CHANNELS,
                        channelLength: CHANNEL_LENGTH,
                        stampBlockSize: CONFIG.STAMP_BLOCK_SIZE,
                        stampLength: STAMP_LENGTH,
                        debugAudio,
                    },
                    outputChannelCount: [CONFIG.CHANNELS],
                });

                if (debugAudio) {
                    node.port.onmessage = (ev) => {
                        const m = ev.data;
                        if (m && m.type === 'phase05-underrun-report') {
                            const tag = m.underrunSamples > 0 || m.staleStampSamples > 0
                                ? '[PHASE0.5 UNDERRUN]'
                                : '[PHASE0.5 ok]';
                            console.log(
                                `${tag} period=${m.periodSamples}sm ` +
                                `underrunSamples=${m.underrunSamples} ` +
                                `underrunQuanta=${m.underrunQuanta} ` +
                                `staleStampSamples=${m.staleStampSamples} ` +
                                `aheadSamples=${m.aheadSamples} ` +
                                `(master=${m.masterClock}, validUntil=${m.validUntil})`
                            );
                        }
                    };
                }

                const gain = new GainNode(ctx, { gain: volumeRef.current });
                node.connect(gain).connect(ctx.destination);
                workletNodeRef.current = node;
                gainNodeRef.current = gain;
            } catch (e) {
                console.error('[SmoothJumpVideoPlayer] audio init failed', e);
                // Continue — without the worklet the clock won't advance and
                // playback stalls. Treat as failure.
                teardownWorker();
                return false;
            }

            // Now that the worker exists, register as a resident so the
            // coordinator can pause / evict us. Eviction handler captures the
            // current frame and tears down asynchronously.
            forcePauseRef.current = () => {
                if (!workerRef.current) return;
                workerRef.current.postMessage({ type: 'SET_PLAYING_INTENT', isPlaying: false });
                if (stateViewRef.current) {
                    lastSamplesRef.current = Atomics.load(stateViewRef.current, MEM_MAP.MASTER_CLOCK);
                }
                isPlayingRef.current = false;
                setIsPlaying(false);
            };
            forceEvictRef.current = () => {
                isPlayingRef.current = false;
                setIsPlaying(false);
                void captureStillAndTeardown();
            };
            coordinator.registerResident(cardId, {
                pause: () => forcePauseRef.current(),
                evict: () => forceEvictRef.current(),
            });

            // NOTE: we deliberately do NOT setShowStill(false) here. The new
            // playback canvas starts transparent, so the still layer shows
            // through until the engine paints the first frame on top of it.
            return true;
        }, [
            cardId,
            captureStillAndTeardown,
            coordinator,
            hasPlayable,
            loopProgram,
            overlays,
            teardownWorker,
        ]);

        // ── Imperative API ───────────────────────────────────────────────────

        const seekToFrame = useCallback((frame: number) => {
            const sv = stateViewRef.current;
            const worker = workerRef.current;
            if (!sv || !worker) return;
            const clamped = Math.max(0, Math.min(frame, totalFrames - 1));
            const seconds = clamped / SMOOTH_PLAYER_TIMELINE_FPS;
            const targetSamples = Math.floor(seconds * CONFIG.SAMPLE_RATE) + 1;
            Atomics.store(sv, MEM_MAP.MASTER_CLOCK, targetSamples);
            Atomics.add(sv, MEM_MAP.AUDIO_EPOCH, 1);
            Atomics.store(sv, MEM_MAP.AUDIO_VALID_UNTIL, targetSamples);
            Atomics.store(sv, MEM_MAP.READ_PTR, targetSamples % CHANNEL_LENGTH);
            Atomics.store(sv, MEM_MAP.WRITE_PTR, targetSamples % CHANNEL_LENGTH);
            lastSamplesRef.current = targetSamples;
            worker.postMessage({ type: 'SEEK', time: seconds, targetSamples });
            updateItemIndexFromCurrent();
        }, [totalFrames, updateItemIndexFromCurrent]);

        const startPlaying = useCallback(async (): Promise<void> => {
            if (!hasPlayable) return;
            coordinator.claimActive(cardId);
            const ok = await ensureWorker();
            if (!ok) return;
            coordinator.touchResident(cardId);
            if (audioCtxRef.current?.state === 'suspended') {
                await audioCtxRef.current.resume().catch(() => {});
            }
            workerRef.current?.postMessage({ type: 'SET_PLAYING_INTENT', isPlaying: true });
            isPlayingRef.current = true;
            setIsPlaying(true);
        }, [cardId, coordinator, ensureWorker, hasPlayable]);

        const pause = useCallback(() => {
            const worker = workerRef.current;
            const sv = stateViewRef.current;
            if (worker) worker.postMessage({ type: 'SET_PLAYING_INTENT', isPlaying: false });
            if (sv) lastSamplesRef.current = Atomics.load(sv, MEM_MAP.MASTER_CLOCK);
            isPlayingRef.current = false;
            setIsPlaying(false);
            coordinator.releaseActive(cardId);
        }, [cardId, coordinator]);

        // Periodically sync item-index state from the master clock so
        // renderControls can show a meaningful itemIndex while playing.
        useEffect(() => profEffect("effect:SmoothJumpVideoPlayer", () => {
            if (!isPlaying) return;
            const handle = setInterval(updateItemIndexFromCurrent, 250);
            return () => clearInterval(handle);
        }), [isPlaying, updateItemIndexFromCurrent]);

        // Fullscreen tracking (event-driven; no polling).
        useEffect(() => profEffect("effect:SmoothJumpVideoPlayer", () => {
            const onChange = () => {
                const inFs = !!document.fullscreenElement
                    && (document.fullscreenElement === canvasHolderRef.current
                        || canvasHolderRef.current?.contains(document.fullscreenElement));
                setIsFullscreen(Boolean(inFs));
            };
            document.addEventListener('fullscreenchange', onChange);
            return () => document.removeEventListener('fullscreenchange', onChange);
        }), []);

        // Unmount cleanup.
        useEffect(() => profEffect("effect:SmoothJumpVideoPlayer", () => {
            return () => {
                coordinator.releaseActive(cardId);
                teardownWorker();
            };
        }), [cardId, coordinator, teardownWorker]);

        useEffect(() => profEffect("effect:SmoothJumpVideoPlayer", () => {
            onPlayStateChange?.(isPlaying);
        }), [isPlaying, onPlayStateChange]);

        useImperativeHandle(ref, () => ({
            async play(options) {
                const mode = options?.mode ?? 'nearest';
                if (mode === 'first') {
                    await startPlaying();
                    seekToFrame(0);
                    return;
                }
                if (mode === 'next') {
                    const ranges = itemRangesRef.current;
                    if (ranges.length > 0) {
                        const curr = itemIndexRef.current;
                        const nextRange = ranges.find((r) => r.itemIdx > curr) ?? ranges[0];
                        await startPlaying();
                        seekToFrame(nextRange.fromFrame);
                        return;
                    }
                }
                // 'nearest' — resume from saved position (or 0 on first call).
                await startPlaying();
            },
            pause() {
                pause();
            },
            async loadItem(index, options) {
                const ranges = itemRangesRef.current;
                const target = ranges.find((r) => r.itemIdx === index) ?? ranges[0];
                if (!target) return;
                if (options?.autoplay) {
                    await startPlaying();
                    seekToFrame(target.fromFrame);
                } else {
                    // Just remember the position; nothing to render until play.
                    lastSamplesRef.current = Math.floor(
                        (target.fromFrame / SMOOTH_PLAYER_TIMELINE_FPS) * CONFIG.SAMPLE_RATE,
                    ) + 1;
                    itemIndexRef.current = target.itemIdx;
                    setItemIndex(target.itemIdx);
                }
            },
            async nextItem(options) {
                const ranges = itemRangesRef.current;
                if (ranges.length === 0) return;
                const curr = itemIndexRef.current;
                const next = ranges.find((r) => r.itemIdx > curr) ?? ranges[0];
                if (options?.autoplay) {
                    await startPlaying();
                    seekToFrame(next.fromFrame);
                } else {
                    itemIndexRef.current = next.itemIdx;
                    setItemIndex(next.itemIdx);
                    lastSamplesRef.current = Math.floor(
                        (next.fromFrame / SMOOTH_PLAYER_TIMELINE_FPS) * CONFIG.SAMPLE_RATE,
                    ) + 1;
                }
            },
            getPlayer() {
                return workerRef.current ?? null;
            },
            getItemIndex() {
                return itemIndexRef.current;
            },
            async enterFullscreen() {
                const el = canvasHolderRef.current;
                if (!el || document.fullscreenElement) return;
                await el.requestFullscreen().catch(() => {});
            },
            async exitFullscreen() {
                if (document.fullscreenElement) {
                    await document.exitFullscreen().catch(() => {});
                }
            },
            isFullscreen() {
                const el = canvasHolderRef.current;
                const fsEl = document.fullscreenElement;
                if (!fsEl) return false;
                return fsEl === el || (el?.contains(fsEl) ?? false);
            },
            async toggleFullscreen() {
                const el = canvasHolderRef.current;
                const fsEl = document.fullscreenElement;
                const inFs = !!fsEl && (fsEl === el || (el?.contains(fsEl) ?? false));
                if (inFs) {
                    await document.exitFullscreen().catch(() => {});
                } else if (el) {
                    await el.requestFullscreen().catch(() => {});
                }
            },
            setMuted(muted) {
                const prev = mutedRef.current;
                mutedRef.current = muted;
                workerRef.current?.postMessage({ type: 'SET_AUDIO_ENABLED', enabled: !muted });
                if (!muted && audioCtxRef.current?.state === 'suspended') {
                    void audioCtxRef.current.resume().catch(() => {});
                }
                // Re-arm gain if we were at 0 from a prior mute toggle.
                if (prev !== muted && gainNodeRef.current) {
                    gainNodeRef.current.gain.value = muted ? 0 : volumeRef.current;
                }
            },
            setVolume(v01) {
                const clamped = Math.max(0, Math.min(1, Number(v01) || 0));
                volumeRef.current = clamped;
                if (gainNodeRef.current && !mutedRef.current) {
                    gainNodeRef.current.gain.value = clamped;
                }
            },
            getMuted() {
                return mutedRef.current;
            },
            getVolume() {
                return volumeRef.current;
            },
        }), [pause, seekToFrame, startPlaying]);

        // ── Render ───────────────────────────────────────────────────────────

        const state: JumpPlayerState = { isPlaying, isFullscreen, itemIndex };

        return (
            <Box
                ref={canvasHolderRef}
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'black',
                    ...sx,
                }}
            >
                {poster && !isPlaying && (
                    <Box
                        component="img"
                        src={poster}
                        alt=""
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            zIndex: 0,
                            pointerEvents: 'none',
                        }}
                    />
                )}
                {/* Still-frame canvas: shown after eviction, hidden once a new
                    worker spawns and overwrites the playback canvas. */}
                <canvas
                    ref={stillCanvasRef}
                    width={1280}
                    height={720}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 1,
                        display: showStill ? 'block' : 'none',
                        pointerEvents: 'none',
                    }}
                />
                {/* Playback canvas. Replaced via React key whenever the worker
                    is (re)spawned, since transferControlToOffscreen is one-shot. */}
                <canvas
                    key={canvasGen}
                    ref={canvasRef}
                    width={1280}
                    height={720}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 2,
                    }}
                />
                {isBuffering && (
                    <Box width="100%" height="100%" position="absolute" top={0} left={0} zIndex={10}>
                        <Box width="100%" height="100%" display="flex" justifyContent="center" alignItems="center">
                            <CircularProgress />
                        </Box>
                    </Box>
                )}
                {renderControls?.({ state })}
            </Box>
        );
    },
);

SmoothJumpVideoPlayer.displayName = 'SmoothJumpVideoPlayer';

export default SmoothJumpVideoPlayer;
